export type PaymentCountryOption = {
  id: number
  name: string | null
  abbreviation: string | null
}

export type PaymentStateOption = {
  id: number
  countryId: number
  name: string | null
  abbreviation: string | null
}

export const DEFAULT_PAYMENT_COUNTRY_ABBREVIATION = "US"
export const MAX_POSTAL_CODE_LENGTH = 16
export const POSTAL_CODE_PATTERN = /^[A-Za-z0-9 -]*$/

export type BillingAddressValidationErrors = {
  firstName?: string
  lastName?: string
  postalCode?: string
  countryId?: string
  stateId?: string
  achAccountType?: string
  achAccountHolderType?: string
}

export type BillingAddressFormFields = {
  firstName: string
  lastName: string
  company: string
  address1: string
  address2: string
  city: string
  stateId: number | null
  postalCode: string
  countryId: number | null
  phone: string
  fax: string
}

export function createEmptyBillingAddressForm(): BillingAddressFormFields {
  return {
    firstName: "",
    lastName: "",
    company: "",
    address1: "",
    address2: "",
    city: "",
    stateId: null,
    postalCode: "",
    countryId: null,
    phone: "",
    fax: "",
  }
}

export function normalizePostalCode(value: string) {
  return value.replace(/\s+/g, " ").trim().slice(0, MAX_POSTAL_CODE_LENGTH)
}

export function getDefaultPaymentCountryId(countries: PaymentCountryOption[]) {
  return (
    countries.find((country) => country.abbreviation?.toUpperCase() === DEFAULT_PAYMENT_COUNTRY_ABBREVIATION)?.id ??
    countries[0]?.id ??
    null
  )
}

export function validatePostalCode(value: string) {
  const normalized = normalizePostalCode(value)

  if (normalized.length === 0) {
    return "Postal code is required."
  }

  if (!POSTAL_CODE_PATTERN.test(normalized)) {
    return "Postal code can only include letters, numbers, spaces, and hyphens."
  }

  return null
}

export function validateBillingAddressFields(
  fields: {
    firstName: string
    lastName: string
    postalCode: string
    countryId: number | null
    stateId: number | null
  },
  hasAvailableStates: boolean
) {
  const errors: BillingAddressValidationErrors = {}

  if (fields.firstName.trim().length === 0) {
    errors.firstName = "First name is required."
  }

  if (fields.lastName.trim().length === 0) {
    errors.lastName = "Last name is required."
  }

  const postalCodeError = validatePostalCode(fields.postalCode)

  if (postalCodeError) {
    errors.postalCode = postalCodeError
  }

  if (fields.countryId === null) {
    errors.countryId = "Country is required."
  }

  if (hasAvailableStates && fields.stateId === null) {
    errors.stateId = "State or province is required."
  }

  return errors
}

export function getPaymentCountryLabel(country: PaymentCountryOption | null | undefined) {
  return country?.name ?? country?.abbreviation ?? ""
}

export function getPaymentStateLabel(state: PaymentStateOption | null | undefined) {
  return state?.abbreviation ?? state?.name ?? ""
}